import React, { useEffect, useState } from 'react';
import {
  Boxes,
  Plus,
  Edit3,
  Search,
  AlertCircle,
  Building2,
  Factory,
  Layers,
  CalendarDays,
  Check,
  Coins,
  Calculator
} from 'lucide-react';
import {
  FixedAsset,
  FixedAssetPayload,
  FixedAssetValuation,
  DepreciationEntry,
  ValuationArea,
  VALUATION_AREA_LABELS
} from '../../../../types/FixedAsset.types';
import { Society } from '../../../../types/Society.types';
import { Supplier } from '../../../../types/Supplier.types';
import { CostCenter } from '../../../../types/CostCenter.types';
import { DepreciationMethod } from '../../../../types/DepreciationMethod.types';
import { useToast } from '../../../../hooks/useToast';
import Modal from '../../../../components/shared/Modal/Modal';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { Skeleton } from '../../../../components/shared/Skeleton/Skeleton';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailSection
} from '../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import {
  listFixedAssets,
  createFixedAsset,
  updateFixedAsset,
  listValuations,
  createValuation,
  listDepreciationEntries,
  postDepreciationEntry
} from '../../../../services/FixedAssetsService';
import { listCompanies } from '../../../../services/CompaniesService';
import store from '../../../../redux/store';
import { listSuppliers } from '../../../../services/SuppliersService';
import { listCostCenters } from '../../../../services/CostCentersService';
import { listDepreciationMethods } from '../../../../services/DepreciationMethodsService';
import { listChartOfAccounts, ChartAccount } from '../../../../services/ChartOfAccountsService';
import { SearchSelect } from '../FinancesView/forms/FormParts';
import { FixedAssetsMonthlyPosting } from './FixedAssetsMonthlyPosting';
import '../shared-modules.css';
import '../FinancesView/masters/FinanceMasters.css';
import './FixedAssetsView.css';

const VALUATION_AREAS: ValuationArea[] = ['accounting', 'fiscal', 'derived'];

const today = () => new Date().toISOString().split('T')[0];

interface AssetFormState {
  companyId: string;
  code: string;
  name: string;
  supplierId: string;
  costCenterId: string;
  assetAccountId: string;
  acquisitionDate: string;
  status: 'active' | 'retired' | 'disposed';
}

interface ValuationFormState {
  acquisitionValue: string;
  depreciationMethodId: string;
  usefulLifeYears: string;
  usefulLifeMonths: string;
  residualValue: string;
  depreciationStartDate: string;
}

const EMPTY_ASSET_FORM: AssetFormState = {
  companyId: '',
  code: '',
  name: '',
  supplierId: '',
  costCenterId: '',
  assetAccountId: '',
  acquisitionDate: today(),
  status: 'active'
};

const emptyValuationForm = (): ValuationFormState => ({
  acquisitionValue: '',
  depreciationMethodId: '',
  usefulLifeYears: '10',
  usefulLifeMonths: '0',
  residualValue: '0',
  depreciationStartDate: today()
});

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value || 0);

export const FixedAssetsView = () => {
  const { notify } = useToast();

  const [assets, setAssets] = useState<FixedAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  // Maestros que alimentan los selects del formulario.
  const [companies, setCompanies] = useState<Society[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [depreciationMethods, setDepreciationMethods] = useState<DepreciationMethod[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);

  const [selectedAsset, setSelectedAsset] = useState<FixedAsset | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [valuations, setValuations] = useState<FixedAssetValuation[]>([]);
  const [valuationsLoading, setValuationsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<ValuationArea | 'schedule'>('accounting');

  const [entries, setEntries] = useState<DepreciationEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [scheduleValuationId, setScheduleValuationId] = useState('');
  const [postModalEntry, setPostModalEntry] = useState<DepreciationEntry | null>(null);

  // Contabilización de las cuotas de un mes, a pantalla completa
  const [isPostingOpen, setIsPostingOpen] = useState(false);

  // Alta / edición del activo
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [assetForm, setAssetForm] = useState<AssetFormState>(EMPTY_ASSET_FORM);
  const [initialValuationForm, setInitialValuationForm] = useState<ValuationFormState>(emptyValuationForm());
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Alta de un área de valoración adicional (Fiscal / Derivada)
  const [newValuationArea, setNewValuationArea] = useState<ValuationArea | null>(null);
  const [newValuationForm, setNewValuationForm] = useState<ValuationFormState>(emptyValuationForm());
  const [valuationError, setValuationError] = useState('');
  const [isSavingValuation, setIsSavingValuation] = useState(false);

  const valuationFor = (area: ValuationArea): FixedAssetValuation | undefined =>
    valuations.filter(v => v.valuationArea === area)[0];

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const list = await listFixedAssets();
      setAssets(list);
      setListError('');
      setSelectedAsset(prev => (prev ? list.find(a => a.id === prev.id) || null : null));
    } catch (err) {
      console.error('Error fetching fixed assets:', err);
      setAssets([]);
      setSelectedAsset(null);
      setListError(err instanceof Error ? err.message : 'No se pudo cargar el inventario de activos fijos.');
    } finally {
      setLoading(false);
    }
  };

  // Los maestros se cargan en paralelo y por separado: que falte uno no debe
  // tumbar la vista entera, solo dejar su select vacío.
  const fetchMasters = async () => {
    listCompanies().then(setCompanies).catch(e => console.error('Error fetching companies:', e));
    listSuppliers().then(setSuppliers).catch(e => console.error('Error fetching suppliers:', e));
    listDepreciationMethods().then(setDepreciationMethods).catch(e => console.error('Error fetching depreciation methods:', e));
    listChartOfAccounts().then(setAccounts).catch(e => console.error('Error fetching chart of accounts:', e));
  };

  // Los centros de coste se piden por sociedad en vez de traerlos todos: el
  // endpoint admite company_id y así el select ya llega filtrado.
  useEffect(() => {
    if (!assetForm.companyId) { setCostCenters([]); return; }
    let cancelled = false;
    listCostCenters({ companyId: assetForm.companyId })
      .then(list => { if (!cancelled) setCostCenters(list); })
      .catch(e => { console.error('Error fetching cost centers:', e); if (!cancelled) setCostCenters([]); });
    return () => { cancelled = true; };
  }, [assetForm.companyId]);

  useEffect(() => {
    fetchAssets();
    fetchMasters();
  }, []);

  const fetchValuations = async (assetId: string) => {
    setValuationsLoading(true);
    try {
      const list = await listValuations(assetId);
      setValuations(list);
    } catch (err) {
      console.error('Error fetching valuations:', err);
      setValuations([]);
    } finally {
      setValuationsLoading(false);
    }
  };

  useEffect(() => {
    setValuations([]);
    setEntries([]);
    setActiveTab('accounting');
    setNewValuationArea(null);
    if (selectedAsset) fetchValuations(selectedAsset.id);
  }, [selectedAsset ? selectedAsset.id : '']);

  const fetchEntries = async (valuationId: string) => {
    setEntriesLoading(true);
    try {
      setEntries(await listDepreciationEntries(valuationId));
    } catch (err) {
      console.error('Error fetching depreciation entries:', err);
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  // El calendario es siempre el de una valoración concreta: al abrir la pestaña
  // se toma la Contable por defecto, que es la obligatoria en el alta.
  const openSchedule = () => {
    setActiveTab('schedule');
    const target = valuationFor('accounting') || valuations[0];
    if (target) {
      setScheduleValuationId(target.id);
      fetchEntries(target.id);
    } else {
      setScheduleValuationId('');
      setEntries([]);
    }
  };

  const handleScheduleValuationChange = (valuationId: string) => {
    setScheduleValuationId(valuationId);
    if (valuationId) fetchEntries(valuationId);
    else setEntries([]);
  };

  const handleCreateNewClick = () => {
    setSelectedAsset(null);
    // Sociedad activa preseleccionada; si no está en la lista, la primera
    const activeId = store.getState().activeCompany.activeId;
    const defaultCompany = companies.find(c => c.id === activeId) || companies[0];
    setAssetForm({ ...EMPTY_ASSET_FORM, companyId: defaultCompany?.id || '' });
    setInitialValuationForm(emptyValuationForm());
    setIsEditing(false);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (asset: FixedAsset) => {
    setSelectedAsset(asset);
    setAssetForm({
      companyId: asset.companyId,
      code: asset.code,
      name: asset.name,
      supplierId: asset.supplierId,
      costCenterId: asset.costCenterId,
      assetAccountId: asset.assetAccountId,
      acquisitionDate: asset.acquisitionDate,
      status: asset.status
    });
    setIsEditing(true);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectAsset = (asset: FixedAsset) => {
    setIsFormOpen(false);
    setSelectedAsset(asset);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedAsset(null);
  };

  const openPosting = () => {
    closePanel();
    setIsPostingOpen(true);
  };

  // Clic fuera cierra la ficha. No cierra el formulario del activo ni la ficha
  // con una valoración a medio crear: esta vista no avisa de cambios sin
  // guardar y se perdería lo escrito; se cierran con la X o su Cancelar.
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedAsset) && !isFormOpen && !newValuationArea);

  const parseValuation = (form: ValuationFormState): { months: number; value: number; residual: number } | null => {
    const years = Number(form.usefulLifeYears || 0);
    const months = Number(form.usefulLifeMonths || 0);
    const value = Number(form.acquisitionValue);
    const residual = Number(form.residualValue || 0);
    if (isNaN(value) || value <= 0) return null;
    if (isNaN(years) || isNaN(months) || (years * 12 + months) <= 0) return null;
    if (isNaN(residual) || residual < 0) return null;
    return { months: years * 12 + months, value, residual };
  };

  const handleSaveAsset = async () => {
    if (!assetForm.companyId) { setFormError('Debe seleccionar una sociedad.'); return; }
    if (!assetForm.code.trim()) { setFormError('El código del activo es obligatorio.'); return; }
    if (!assetForm.name.trim()) { setFormError('La denominación es obligatoria.'); return; }
    if (!assetForm.acquisitionDate) { setFormError('La fecha de adquisición es obligatoria.'); return; }

    const payload: FixedAssetPayload = {
      companyId: assetForm.companyId,
      code: assetForm.code.trim().toUpperCase(),
      name: assetForm.name.trim(),
      supplierId: assetForm.supplierId,
      costCenterId: assetForm.costCenterId,
      assetAccountId: assetForm.assetAccountId,
      acquisitionDate: assetForm.acquisitionDate,
      status: assetForm.status
    };

    // En el alta la valoración Contable es obligatoria, así que se valida antes
    // de crear nada: no queremos un activo huérfano sin área contable.
    let parsed: { months: number; value: number; residual: number } | null = null;
    if (!isEditing) {
      parsed = parseValuation(initialValuationForm);
      if (!parsed) {
        setFormError('La valoración inicial necesita valor de adquisición, vida útil y valor residual válidos.');
        return;
      }
      if (!initialValuationForm.depreciationMethodId) {
        setFormError('Debe seleccionar una clave de amortización para la valoración inicial.');
        return;
      }
    }

    setIsSaving(true);
    try {
      if (isEditing && selectedAsset) {
        const updated = await updateFixedAsset(selectedAsset.id, payload);
        setAssets(prev => prev.map(a => (a.id === updated.id ? updated : a)));
        setSelectedAsset(updated);
        notify('Éxito', 'Activo fijo modificado exitosamente', 'info');
      } else {
        const created = await createFixedAsset(payload);
        await createValuation({
          fixedAssetId: created.id,
          valuationArea: 'accounting',
          acquisitionValue: parsed!.value,
          depreciationMethodId: initialValuationForm.depreciationMethodId,
          usefulLifeMonths: parsed!.months,
          residualValue: parsed!.residual,
          depreciationStartDate: initialValuationForm.depreciationStartDate
        });
        setAssets(prev => [created, ...prev]);
        setSelectedAsset(created);
        notify('Éxito', 'Activo fijo dado de alta con su valoración contable', 'info');
      }
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error saving fixed asset:', err);
      setFormError(err instanceof Error ? err.message : 'Error de comunicación con el servidor.');
    } finally {
      setIsSaving(false);
    }
  };

  const openNewValuation = (area: ValuationArea) => {
    setNewValuationArea(area);
    setNewValuationForm(emptyValuationForm());
    setValuationError('');
  };

  const handleSaveValuation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAsset || !newValuationArea) return;

    const parsed = parseValuation(newValuationForm);
    if (!parsed) {
      setValuationError('Revise el valor de adquisición, la vida útil y el valor residual.');
      return;
    }
    if (!newValuationForm.depreciationMethodId) {
      setValuationError('Debe seleccionar una clave de amortización.');
      return;
    }

    setIsSavingValuation(true);
    try {
      const created = await createValuation({
        fixedAssetId: selectedAsset.id,
        valuationArea: newValuationArea,
        acquisitionValue: parsed.value,
        depreciationMethodId: newValuationForm.depreciationMethodId,
        usefulLifeMonths: parsed.months,
        residualValue: parsed.residual,
        depreciationStartDate: newValuationForm.depreciationStartDate
      });
      setValuations(prev => [...prev, created]);
      setActiveTab(newValuationArea);
      setNewValuationArea(null);
      notify('Éxito', `Valoración ${VALUATION_AREA_LABELS[created.valuationArea]} creada exitosamente`, 'info');
    } catch (err) {
      console.error('Error creating valuation:', err);
      setValuationError(err instanceof Error ? err.message : 'Error al crear la valoración.');
    } finally {
      setIsSavingValuation(false);
    }
  };

  const confirmPostEntry = async () => {
    if (!postModalEntry) return;
    const entry = postModalEntry;
    setPostModalEntry(null);
    try {
      const posted = await postDepreciationEntry(entry.id);
      setEntries(prev => prev.map(e => (e.id === posted.id ? posted : e)));
      notify('Éxito', `Cuota ${entry.fiscalYear}/${entry.period} contabilizada`, 'info');
    } catch (err) {
      console.error('Error posting depreciation entry:', err);
      notify('Error', err instanceof Error ? err.message : 'Error al contabilizar la cuota.', 'urgent');
    }
  };

  const filteredAssets = assets.filter(a =>
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns: ColumnDef<FixedAsset>[] = [
    {
      id: 'code',
      header: 'CÓDIGO',
      render: (asset) => <span className="fixed-assets-view__code">{asset.code}</span>
    },
    {
      id: 'name',
      header: 'DENOMINACIÓN',
      render: (asset) => (
        <>
          <div className="fixed-assets-view__name">{asset.name}</div>
          <div className="fixed-assets-view__sub">Alta: {asset.acquisitionDate || '—'}</div>
        </>
      )
    },
    {
      id: 'company',
      header: 'SOCIEDAD / ACREEDOR',
      render: (asset) => (
        <>
          <div className="fixed-assets-view__sub-bright">{asset.companyName || '—'}</div>
          <div className="fixed-assets-view__sub">{asset.supplierName || 'Sin acreedor'}</div>
        </>
      )
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (asset) => (
        <span className={`module-badge ${asset.status === 'active' ? 'active' : 'inactive'}`}>
          {asset.status}
        </span>
      )
    }
  ];

  // Ya vienen filtrados del servicio por la sociedad seleccionada.
  const costCentersForForm = costCenters;

  // El valor es el id, no el código: asset_account_id es FK a chart_of_accounts.
  // Ordenadas por código para que salgan en orden de PGC (2, 21, 210, 2100...).
  const assetAccountOptions = [
    { value: '', label: '— Sin cuenta de mayor —' },
    ...[...accounts]
      .sort((a, b) => a.code.localeCompare(b.code))
      .map(a => ({ value: a.id, label: `${a.code} · ${a.name}` }))
  ];

  // Si se escriben dígitos se busca por el comienzo del código (222 → 222x);
  // si se escribe texto, por el nombre en cualquier posición.
  const matchAccount = (option: { label: string }, q: string) =>
    /^\d+$/.test(q) ? option.label.startsWith(q) : option.label.toLowerCase().includes(q);

  const renderValuationFields = (
    form: ValuationFormState,
    setForm: (updater: (prev: ValuationFormState) => ValuationFormState) => void
  ) => (
    <>
      <div className="sidebar-form-grid-2">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Valor de Adquisición (€) *</label>
          <input
            type="number" min="0" step="0.01" required
            value={form.acquisitionValue}
            onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, acquisitionValue: v })); }}
            className="sidebar-form-input"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Clave de Amortización *</label>
          <select
            value={form.depreciationMethodId}
            onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, depreciationMethodId: v })); }}
            className="sidebar-form-input"
          >
            <option value="">— Seleccione una clave —</option>
            {depreciationMethods.map(m => (
              <option key={m.id} value={m.id}>{m.code} — {m.name} ({m.annualRatePct}%)</option>
            ))}
          </select>
        </div>
      </div>

      <div className="sidebar-form-grid-2">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Vida Útil (años)</label>
          <input
            type="number" min="0" step="1"
            value={form.usefulLifeYears}
            onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, usefulLifeYears: v })); }}
            className="sidebar-form-input"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Vida Útil (meses)</label>
          <input
            type="number" min="0" max="11" step="1"
            value={form.usefulLifeMonths}
            onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, usefulLifeMonths: v })); }}
            className="sidebar-form-input"
          />
        </div>
      </div>

      <div className="sidebar-form-grid-2">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Valor Residual (€)</label>
          <input
            type="number" min="0" step="0.01"
            value={form.residualValue}
            onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, residualValue: v })); }}
            className="sidebar-form-input"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha de Inicio del Cálculo *</label>
          <input
            type="date" required
            value={form.depreciationStartDate}
            onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, depreciationStartDate: v })); }}
            className="sidebar-form-input"
          />
        </div>
      </div>
    </>
  );

  // Los skeletons reutilizan las clases de layout del bloque real (mismo grid,
  // mismos paneles, misma tabla) para que al llegar los datos no salte la
  // silueta: solo se sustituye el contenido de cada hueco.
  // Aquí las etiquetas NO se pintan: el bloque entero es condicional — si el
  // activo no tiene valoración en el área, en su lugar sale el aviso de "sin
  // valoración" con el botón de crearla. Anunciar VALOR_ADQUISICIÓN y demás
  // sería prometer una estructura que puede no llegar nunca.
  const renderValuationSkeleton = () => (
    <div className="module-space-y-3">
      <div className="fixed-assets-view__val-grid">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="fixed-assets-view__val-card">
            <div className="skeleton-block skeleton-block--tight">
              <Skeleton width="60%" height="7px" />
              <Skeleton width="80%" height="12px" />
            </div>
          </div>
        ))}
      </div>
      <div className="detail-field-list">
        {[0, 1].map(i => (
          <div key={i} className="detail-field">
            <Skeleton width="40%" height="10px" />
            <Skeleton width="35%" height="10px" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderScheduleSkeleton = () => (
    <div className="fixed-assets-view__table-wrapper">
      <table className="fixed-assets-view__table">
        <thead>
          <tr>
            <th>EJERCICIO</th>
            <th className="center">PERIODO</th>
            <th className="right">CUOTA</th>
            <th className="center">ESTADO</th>
            <th className="right">ACCIÓN</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i} className="fixed-assets-view__table-row">
              <td><Skeleton /></td>
              <td className="center"><Skeleton /></td>
              <td className="right"><Skeleton /></td>
              <td className="center"><Skeleton /></td>
              <td className="right"><Skeleton width="24px" height="14px" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderValuationDetail = (area: ValuationArea) => {
    const valuation = valuationFor(area);

    if (newValuationArea === area) {
      return (
        <form onSubmit={handleSaveValuation}>
          <DetailSection icon={Coins} title={`Nueva valoración ${VALUATION_AREA_LABELS[area]}`}>
            {valuationError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{valuationError}</span>
              </div>
            )}
            {renderValuationFields(newValuationForm, setNewValuationForm)}
            <div className="module-form-actions">
              <button type="button" onClick={() => setNewValuationArea(null)} className="module-form-cancel-btn">Cancelar</button>
              <button type="submit" disabled={isSavingValuation} className="module-form-submit-btn">
                {isSavingValuation ? 'Guardando...' : 'Crear_Valoración'}
              </button>
            </div>
          </DetailSection>
        </form>
      );
    }

    if (!valuation) {
      return (
        <div className="fixed-assets-view__no-valuation">
          <p>Este activo no tiene valoración {VALUATION_AREA_LABELS[area]}.</p>
          <button type="button" onClick={() => openNewValuation(area)} className="module-create-btn">
            <Plus size={14} /> Crear valoración {VALUATION_AREA_LABELS[area]}
          </button>
        </div>
      );
    }

    const netValue = valuation.acquisitionValue - valuation.accumulatedDepreciation;

    return (
      <div className="module-space-y-3">
        <div className="fixed-assets-view__val-grid">
          <div className="fixed-assets-view__val-card">
            <span className="fixed-assets-view__val-label">VALOR_ADQUISICIÓN</span>
            <span className="fixed-assets-view__val-value">{formatCurrency(valuation.acquisitionValue)}</span>
          </div>
          <div className="fixed-assets-view__val-card">
            <span className="fixed-assets-view__val-label">AMORT_ACUMULADA</span>
            <span className="fixed-assets-view__val-value">{formatCurrency(valuation.accumulatedDepreciation)}</span>
          </div>
          <div className="fixed-assets-view__val-card">
            <span className="fixed-assets-view__val-label">VALOR_NETO_CONTABLE</span>
            <span className="fixed-assets-view__val-value green">{formatCurrency(netValue)}</span>
          </div>
          <div className="fixed-assets-view__val-card">
            <span className="fixed-assets-view__val-label">VALOR_RESIDUAL</span>
            <span className="fixed-assets-view__val-value">{formatCurrency(valuation.residualValue)}</span>
          </div>
        </div>

        <DetailFieldList>
          <DetailField icon={Layers} label="CLAVE_AMORTIZACIÓN">
            {`${valuation.depreciationMethodCode ? `${valuation.depreciationMethodCode} — ` : ''}${valuation.depreciationMethodName || 'No declarada'}`}
          </DetailField>
          <DetailField icon={CalendarDays} label="VIDA_ÚTIL / INICIO_CÁLCULO">
            {`${Math.floor(valuation.usefulLifeMonths / 12)} años ${valuation.usefulLifeMonths % 12} meses · desde ${valuation.depreciationStartDate || '—'}`}
          </DetailField>
        </DetailFieldList>
      </div>
    );
  };

  // Mientras está abierta sustituye al inventario
  if (isPostingOpen) return <FixedAssetsMonthlyPosting onClose={() => setIsPostingOpen(false)} />;

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="FILTRAR POR CÓDIGO, DENOMINACIÓN O ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <div className="fixed-assets-view__toolbar-actions">
          <button onClick={openPosting} className="module-create-btn" data-keep-detail>
            <Calculator size={14} /> Contabilizar_Activos
          </button>
          <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
            <Plus size={14} /> Nuevo_Activo
          </button>
        </div>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedAsset || isFormOpen)}>
        {listError ? (
          <div className="module-panel">
            <div className="module-panel-empty">{listError}</div>
          </div>
        ) : (
          <DataTable
            data={filteredAssets}
            columns={columns}
            title={<><Boxes size={14} className="module-panel-title-icon" /> Inventario_De_Activos</>}
            subtitle={loading ? undefined : `TOTAL: ${filteredAssets.length}`}
            isLoading={loading}
            getRowId={(asset) => asset.id}
            onRowClick={handleSelectAsset}
            selectedRowId={selectedAsset?.id}
            emptyText="No se encontraron activos fijos registrados."
            actionsHeader="ACCIONES"
            actionsColumn={(asset) => (
              <RowActionsMenu
                actions={[
                  viewAction(() => handleSelectAsset(asset)),
                  editAction(() => handleEditClick(asset))
                ]}
              />
            )}
          />
        )}

        {isFormOpen ? (
          <SidebarForm
            expandable
            title={isEditing ? 'Modificar_Activo_Fijo' : 'Alta_De_Nuevo_Activo'}
            onClose={closePanel}
            submitLabel={isSaving ? 'Guardando...' : 'Guardar_Activo'}
            submitDisabled={isSaving}
            onSubmit={handleSaveAsset}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection icon={Building2} title="1. Identificación_Del_Activo">
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Sociedad *</label>
                  <select
                    value={assetForm.companyId}
                    disabled={isEditing}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Al cambiar de sociedad el centro de coste deja de ser válido.
                      setAssetForm(prev => ({ ...prev, companyId: v, costCenterId: '' }));
                    }}
                    className="sidebar-form-input"
                    title={isEditing ? 'La sociedad no se puede cambiar tras el alta' : ''}
                  >
                    <option value="">— Seleccione una sociedad —</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Código del Activo *</label>
                  <input
                    type="text" required
                    value={assetForm.code}
                    disabled={isEditing}
                    onChange={(e) => { const v = e.target.value.toUpperCase(); setAssetForm(prev => ({ ...prev, code: v })); }}
                    placeholder="AF-2026-001"
                    className="sidebar-form-input"
                    title={isEditing ? 'El código no se puede cambiar tras el alta' : ''}
                  />
                </div>
              </div>

              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Denominación *</label>
                <input
                  type="text" required
                  value={assetForm.name}
                  onChange={(e) => { const v = e.target.value; setAssetForm(prev => ({ ...prev, name: v })); }}
                  placeholder="Maquinaria de impresión offset"
                  className="sidebar-form-input"
                />
              </div>

              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Acreedor</label>
                  <select
                    value={assetForm.supplierId}
                    onChange={(e) => { const v = e.target.value; setAssetForm(prev => ({ ...prev, supplierId: v })); }}
                    className="sidebar-form-input"
                  >
                    <option value="">— Sin acreedor —</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Centro de Coste</label>
                  <select
                    value={assetForm.costCenterId}
                    onChange={(e) => { const v = e.target.value; setAssetForm(prev => ({ ...prev, costCenterId: v })); }}
                    className="sidebar-form-input"
                  >
                    <option value="">— Sin centro de coste —</option>
                    {costCentersForForm.map(cc => (
                      <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Cuenta de Mayor</label>
                  <SearchSelect
                    options={assetAccountOptions}
                    value={assetForm.assetAccountId}
                    onChange={(v) => setAssetForm(prev => ({ ...prev, assetAccountId: v }))}
                    matches={matchAccount}
                    placeholder="Buscar cuenta por código o nombre..."
                    emptyText="No hay cuentas en el plan"
                    fallbackLabel={isEditing && selectedAsset ? selectedAsset.assetAccountName : ''}
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Fecha de Adquisición *</label>
                  <input
                    type="date" required
                    value={assetForm.acquisitionDate}
                    onChange={(e) => { const v = e.target.value; setAssetForm(prev => ({ ...prev, acquisitionDate: v })); }}
                    className="sidebar-form-input"
                  />
                </div>
              </div>

              {isEditing && (
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Estado</label>
                  <select
                    value={assetForm.status}
                    onChange={(e) => { const v = e.target.value as AssetFormState['status']; setAssetForm(prev => ({ ...prev, status: v })); }}
                    className="sidebar-form-input"
                  >
                    <option value="active">ACTIVO</option>
                    <option value="retired">RETIRADO</option>
                    <option value="disposed">DADO DE BAJA</option>
                  </select>
                </div>
              )}
            </DetailSection>

            {!isEditing && (
              <DetailSection icon={Coins} title="2. Valoración_Inicial (Área Contable)">
                {renderValuationFields(initialValuationForm, setInitialValuationForm)}
              </DetailSection>
            )}
          </SidebarForm>
        ) : selectedAsset ? (
          <DetailPanel
            expandable
            title="Ficha_De_Activo"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedAsset)} />
            }
            cards={
              <>
                <DetailCard icon={Coins} title="Valoración_Y_Amortización">
                  <div className="fixed-assets-view__tabs">
                    {VALUATION_AREAS.map(area => (
                      <button
                        key={area}
                        type="button"
                        onClick={() => { setActiveTab(area); setNewValuationArea(null); }}
                        className={`fixed-assets-view__tab ${activeTab === area ? 'active' : ''}`}
                      >
                        {VALUATION_AREA_LABELS[area]}
                        {!valuationFor(area) && <span className="fixed-assets-view__tab-dot" title="Sin valoración" />}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={openSchedule}
                      className={`fixed-assets-view__tab ${activeTab === 'schedule' ? 'active' : ''}`}
                    >
                      Calendario
                    </button>
                  </div>

                  {valuationsLoading ? (
                    renderValuationSkeleton()
                  ) : activeTab === 'schedule' ? (
                    <div className="module-space-y-3">
                      <div className="sidebar-form-group">
                        <label className="sidebar-form-label">Área de valoración del calendario</label>
                        <select
                          value={scheduleValuationId}
                          onChange={(e) => handleScheduleValuationChange(e.target.value)}
                          className="sidebar-form-input"
                        >
                          <option value="">— Seleccione un área —</option>
                          {valuations.map(v => (
                            <option key={v.id} value={v.id}>{VALUATION_AREA_LABELS[v.valuationArea]}</option>
                          ))}
                        </select>
                      </div>

                      {entriesLoading ? (
                        renderScheduleSkeleton()
                      ) : entries.length === 0 ? (
                        <div className="module-panel-empty">
                          {scheduleValuationId
                            ? 'Esta valoración no tiene cuotas de amortización calculadas.'
                            : 'Seleccione un área de valoración para ver su calendario.'}
                        </div>
                      ) : (
                        <div className="fixed-assets-view__table-wrapper">
                          <table className="fixed-assets-view__table">
                            <thead>
                              <tr>
                                <th>EJERCICIO</th>
                                <th className="center">PERIODO</th>
                                <th className="right">CUOTA</th>
                                <th className="center">ESTADO</th>
                                <th className="right">ACCIÓN</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entries.map(entry => (
                                <tr key={entry.id} className="fixed-assets-view__table-row">
                                  <td><span className="fixed-assets-view__code">{entry.fiscalYear}</span></td>
                                  <td className="center">{entry.period}</td>
                                  <td className="right">{formatCurrency(entry.plannedAmount)}</td>
                                  <td className="center">
                                    <span className={`module-badge ${entry.status === 'posted' ? 'completed' : 'pending'}`}>
                                      {entry.status === 'posted' ? 'CONTABILIZADO' : 'PREVISTO'}
                                    </span>
                                  </td>
                                  <td className="right">
                                    {entry.status === 'planned' && (
                                      <button
                                        onClick={() => setPostModalEntry(entry)}
                                        className="module-action-btn"
                                        title="Contabilizar esta cuota"
                                      >
                                        <Check size={12} />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : (
                    renderValuationDetail(activeTab)
                  )}
                </DetailCard>
              </>
            }
          >
            <DetailIdentity
              icon={Boxes}
              reference={selectedAsset.code}
              name={selectedAsset.name}
              subtitle={
                <span className={`module-badge ${selectedAsset.status === 'active' ? 'active' : 'inactive'}`}>
                  {selectedAsset.status}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={Building2} label="SOCIEDAD">{selectedAsset.companyName || '—'}</DetailField>
              <DetailField icon={Factory} label="ACREEDOR">{selectedAsset.supplierName || 'Sin acreedor'}</DetailField>
              <DetailField icon={Layers} label="CENTRO_DE_COSTE">{selectedAsset.costCenterName || 'Sin centro de coste'}</DetailField>
              <DetailField icon={CalendarDays} label="FECHA_ADQUISICIÓN">{selectedAsset.acquisitionDate || '—'}</DetailField>
            </DetailFieldList>

            {/* Áreas de valoración + calendario */}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      <Modal
        isOpen={!!postModalEntry}
        type="warning"
        title="Contabilizar cuota de amortización"
        message={
          postModalEntry
            ? `Se marcará como contabilizada la cuota del periodo ${postModalEntry.period} del ejercicio ${postModalEntry.fiscalYear} por ${formatCurrency(postModalEntry.plannedAmount)}. Esta acción no se puede deshacer.`
            : ''
        }
        confirmText="Contabilizar"
        cancelText="Cancelar"
        onConfirm={confirmPostEntry}
        onCancel={() => setPostModalEntry(null)}
      />
    </div>
  );
};
